import { composeRefs } from '@radix-ui/react-compose-refs'
import * as React from 'react'
import { cn } from '../../utils/cn'
import { LineIcon } from '../line-icon'
import { useFileUploadContext } from './file-upload-context'

export function FileUploadImageInput({
  accept = 'image/*',
  className,
  disabled,
  ref,
  variant = 'outline',
  onChange,
  onDragLeave,
  onDragOver,
  onDrop,
  ...props
}: FileUploadImageInput.Props) {
  const { id, name, size, value, setValue } = useFileUploadContext()
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const [dragActive, setDragActive] = React.useState(false)

  return (
    <button
      aria-disabled={disabled}
      className={cn(
        // Base
        'group/file-upload-image-input relative cursor-pointer overflow-hidden after:pointer-events-none after:absolute after:inset-0',
        // Focus
        'focus-visible:after:ring-accent-600 focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset',
        // Disabled
        'disabled:cursor-not-allowed disabled:opacity-50',
        // Size
        {
          sm: 'h-7 min-w-7 rounded-sm after:rounded-sm',
          md: 'h-9 min-w-9 rounded-md after:rounded-md',
        }[size],
        // Variant
        {
          ghost: 'hover:bg-gray-100 focus-visible:bg-gray-100',
          outline: 'bg-background shadow-xs p-px after:ring after:ring-inset after:ring-gray-200',
          soft: 'bg-gray-100 hover:bg-gray-200',
        }[variant],
        // Drag state
        dragActive && 'bg-accent-50 after:ring-accent-600',
        className
      )}
      disabled={disabled}
      ref={buttonRef}
      role="button"
      type="button"
      onClick={() => {
        if (disabled) return
        inputRef.current?.click()
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) {
          setDragActive(true)
          buttonRef.current?.focus()
        }
        onDragOver?.(e)
      }}
      onDragLeave={(e) => {
        e.preventDefault()
        // Only set drag inactive if we're leaving the button entirely
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setDragActive(false)
        }
        onDragLeave?.(e)
      }}
      onDrop={(e) => {
        e.preventDefault()
        setDragActive(false)
        const files = e.dataTransfer.files
        const file = files[0]
        if (disabled) return
        if (!files.length) return
        if (!file) return
        setValue(file)
        onDrop?.(e)
      }}
      {...props}
    >
      {value ? (
        <img
          className="size-full object-cover"
          src={value}
          alt="Selected image preview"
          role="img"
        />
      ) : (
        <div
          className={cn(
            'flex size-full items-center justify-center',
            {
              ghost: 'text-gray-400',
              outline: 'text-gray-400',
              soft: 'text-gray-500',
            }[variant]
          )}
          role="presentation"
        >
          <LineIcon.Image className="size-[55%]" aria-hidden="true" />
        </div>
      )}
      <div
        aria-hidden="true"
        className="invisible absolute inset-0 z-0 flex items-center justify-center group-hover/file-upload-image-input:visible group-hover/file-upload-image-input:bg-black/40"
      >
        <LineIcon.Pen className="size-[45%] text-white" />
      </div>
      <input
        accept={accept}
        aria-hidden="true"
        className="sr-only"
        disabled={disabled}
        id={id}
        name={name}
        ref={composeRefs(inputRef, ref)}
        type="file"
        tabIndex={-1}
        onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
          // Prevents that onChange gets triggered when selection gets canceled
          const files = e.target.files
          if (!files?.length) return
          const file = files[0]
          if (!file) return
          setValue(file)
          onChange?.(e)
        }}
      />
    </button>
  )
}

export namespace FileUploadImageInput {
  export interface Props
    extends Omit<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      'id' | 'name' | 'value' | 'onChange'
    > {
    accept?: string
    disabled?: boolean
    placeholder?: React.ReactElement
    ref?: React.Ref<HTMLInputElement>
    variant?: 'ghost' | 'outline' | 'soft'
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  }
}
